/**
 * Prüfen und Vormerken im Monats-Editor: prüft die Felder (sonst gingen ungültige Eingaben still als
 * „unverändert“ raus), öffnet den Prüfschritt mit genau der Nutzlast, die gesendet wird, und lädt
 * nach dem Vormerken auch die Preisautomatik-Schicht nach.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAriPushBatch } from '@/features/properties/ari-edit';
import { problemText, useApiProblem, type AriPushBatchRange } from '@/shared/api';
import { hasAnyValue, toBatchRanges, type InlineEditorValues } from '../calendar/inlineEditorModel';
import type { SelectedRange } from '../calendar/rangeSelectionModel';
import { invalidFields } from './monthReview';

export function useMonthPush(onSuccess: () => void) {
  const queryClient = useQueryClient();
  const [review, setReview] = useState<AriPushBatchRange[] | null>(null);
  const push = useAriPushBatch({
    onSuccess: () => {
      setReview(null);
      void queryClient.invalidateQueries({ queryKey: ['dynamic-pricing-calendar'] });
      onSuccess();
    },
  });
  const problem = useApiProblem(push.error);
  return {
    review,
    push,
    pushError: push.isError
      ? problemText(problem, 'Die Änderungen konnten nicht vorgemerkt werden.')
      : null,
    close: () => setReview(null),
    start: (ranges: readonly SelectedRange[], values: InlineEditorValues) => {
      const batch = toBatchRanges(ranges, values);
      const errors = invalidFields(values, batch[0]);
      if (errors.length > 0) toast.error(errors.join(' · '));
      else if (!hasAnyValue(values))
        toast.error('Setze mindestens einen Wert — leere Felder bleiben unverändert.');
      else {
        push.reset();
        setReview(batch);
      }
    },
  };
}
